import React, { useState, useRef, useEffect } from 'react';
import { useLibrary } from '../context/LibraryContext';

const STATUS_CONFIG = {
  want: {
    label: 'Want to Read',
    emoji: '📚',
    color: 'text-blue-400',
    bg: 'bg-blue-900/30',
    border: 'border-blue-700/50',
  },
  reading: {
    label: 'Currently Reading',
    emoji: '📖',
    color: 'text-yellow-400',
    bg: 'bg-yellow-900/30',
    border: 'border-yellow-700/50',
  },
  finished: {
    label: 'Finished',
    emoji: '✅',
    color: 'text-green-400',
    bg: 'bg-green-900/30',
    border: 'border-green-700/50',
  },
};

function ReadingStatusBadge({ book }) {
  const { setReadingStatus, getReadingStatus } = useLibrary();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  if (!book) return null;

  const currentStatus = getReadingStatus(book.id);
  const config = currentStatus ? STATUS_CONFIG[currentStatus] : null;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (status) => {
    setReadingStatus(book, status);
    setOpen(false);
  };

  const handleRemove = () => {
    setReadingStatus(book, null);
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`
          inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium
          border transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-transparent
          ${config
            ? `${config.bg} ${config.border} ${config.color} hover:opacity-80`
            : 'bg-surface border-border text-gray-300 hover:border-accent/50 hover:text-accent'
          }
        `}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {config ? (
          <>
            <span>{config.emoji}</span>
            <span>{config.label}</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add to List</span>
          </>
        )}
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute left-0 top-full mt-1.5 z-50 min-w-[200px] rounded-xl overflow-hidden
                     shadow-2xl border border-border"
          style={{ background: '#1a1a2e' }}
          role="listbox"
        >
          {Object.entries(STATUS_CONFIG).map(([statusKey, cfg]) => (
            <button
              key={statusKey}
              type="button"
              role="option"
              aria-selected={currentStatus === statusKey}
              onClick={() => handleSelect(statusKey)}
              className={`
                w-full flex items-center gap-3 px-4 py-3 text-sm text-left
                transition-colors duration-150
                ${currentStatus === statusKey
                  ? `${cfg.bg} ${cfg.color}`
                  : 'text-gray-300 hover:bg-white/5 hover:text-white'
                }
              `}
            >
              <span className="text-base">{cfg.emoji}</span>
              <span className="font-medium">{cfg.label}</span>
              {currentStatus === statusKey && (
                <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}

          {/* Remove option */}
          {currentStatus && (
            <>
              <div className="border-t border-border/50 mx-3" />
              <button
                type="button"
                onClick={handleRemove}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left
                           text-gray-500 hover:text-red-400 hover:bg-red-900/10
                           transition-colors duration-150"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Remove from List</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ReadingStatusBadge;
